import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { ENGAGEMENT_STAGES, STAGE_LABELS, DATA_CLASSIFICATIONS } from '../../shared/stages';
import { ErrorNote, ScoreDonut, Spinner, StagePill } from '../components/ui';
import type { ScoreComponent } from '../../shared/scoring';
import { OverviewTab } from './tabs/OverviewTab';
import { AssessmentTab } from './tabs/AssessmentTab';
import { UseCasesTab } from './tabs/UseCasesTab';
import { NistTab } from './tabs/NistTab';
import { RisksTab } from './tabs/RisksTab';
import { DeliverablesTab } from './tabs/DeliverablesTab';

export interface EngagementFull {
  id: string;
  company_name: string;
  company_domain: string | null;
  industry: string | null;
  company_size: string | null;
  contact_name: string;
  contact_email: string;
  contact_role: string | null;
  role_level: string | null;
  stage: string;
  recommended_offer: string | null;
  offer_signals: string | null;
  fit_score: number | null;
  fit_breakdown: string | null;
  urgency_score: number | null;
  urgency_breakdown: string | null;
  data_classification: string;
  primary_goal: string | null;
  current_ai_tools: string | null;
  ai_maturity_self_rating: number | null;
  regulatory_exposure: string | null;
  timeline: string | null;
  budget_range: string | null;
  compelling_event: string | null;
  notes: string | null;
  hubspot_contact_id: string | null;
  hubspot_company_id: string | null;
  hubspot_deal_id: string | null;
  hubspot_synced_at: number | null;
  created_at: number;
  updated_at: number;
}

export interface ArtifactMeta {
  id: string;
  type: string;
  title: string;
  ai_engine: string | null;
  template_version: string | null;
  review_status: string;
  created_at: number;
}

export interface EngagementBundle {
  engagement: EngagementFull;
  artifacts: ArtifactMeta[];
  counts: {
    useCases: number;
    assessedDimensions: number;
    assessmentAvg: number | null;
    openRisks: number;
    nistMappings: number;
  };
  outbox: Array<{ id: string; kind: string; status: string; attempts: number; last_error: string | null }>;
}

const TABS = ['Overview', 'Assessment', 'Use Cases', 'NIST', 'Risks', 'Deliverables'] as const;
export type TabName = (typeof TABS)[number];

function Breakdown({ json, title }: { json: string | null; title: string }) {
  if (!json) return null;
  let components: ScoreComponent[] = [];
  try { components = JSON.parse(json) as ScoreComponent[]; } catch { return null; }
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-slate-500 hover:text-slate-300">{title} breakdown</summary>
      <ul className="mt-1 space-y-0.5 rounded-lg border border-slate-800 bg-slate-900/80 p-2">
        {components.map((c) => (
          <li key={c.key} className="flex justify-between gap-3">
            <span className="text-slate-400">{c.label}</span>
            <span className="text-slate-200">{c.points}/{c.max} <span className="text-slate-500">— {c.why}</span></span>
          </li>
        ))}
      </ul>
    </details>
  );
}

export function EngagementDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as TabName) || 'Overview';
  const [bundle, setBundle] = useState<EngagementBundle | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const b = await api.get<EngagementBundle>(`/api/engagements/${id}`);
      setBundle(b);
    } catch {
      setError('Engagement not found.');
    }
  }, [id]);

  useEffect(() => { void reload(); }, [reload]);

  async function patch(fields: Record<string, unknown>) {
    if (!id) return;
    setSaving(true);
    try {
      const r = await api.patch<{ engagement: EngagementFull }>(`/api/engagements/${id}`, fields);
      setBundle((b) => (b ? { ...b, engagement: r.engagement } : b));
    } catch {
      setError('Update failed.');
    } finally {
      setSaving(false);
    }
  }

  if (error) return <div><ErrorNote message={error} /><Link className="text-sm text-brand-300" to="/app">← Back</Link></div>;
  if (!bundle) return <Spinner />;
  const e = bundle.engagement;
  const pendingSync = bundle.outbox.some((o) => o.status === 'pending');

  return (
    <div>
      <Link to="/app" className="text-xs text-slate-500 hover:text-slate-300">← Engagements</Link>

      <div className="mt-2 flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-50">{e.company_name}</h1>
            <StagePill stage={e.stage} />
            {e.hubspot_deal_id ? (
              <span className="chip border-green-700 bg-green-950/40 text-green-300">CRM synced</span>
            ) : pendingSync ? (
              <span className="chip border-amber-700 bg-amber-950/40 text-amber-300">CRM queued</span>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-slate-400">
            {e.contact_name}{e.contact_role ? ` · ${e.contact_role}` : ''} · {e.contact_email}
            {e.industry ? ` · ${e.industry}` : ''}{e.company_size ? ` · ${e.company_size}` : ''}
          </p>
          <div className="mt-2 flex flex-wrap gap-4">
            <Breakdown json={e.fit_breakdown} title="Fit" />
            <Breakdown json={e.urgency_breakdown} title="Urgency" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ScoreDonut score={e.fit_score} label="Fit" />
          <ScoreDonut score={e.urgency_score} label="Urgency" />
          <div className="space-y-2">
            <div>
              <label className="label !mb-0.5">Stage</label>
              <select
                className="input !w-52 !py-1.5"
                value={e.stage}
                disabled={saving}
                onChange={(ev) => void patch({ stage: ev.target.value })}
              >
                {ENGAGEMENT_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
              </select>
            </div>
            <div>
              <label className="label !mb-0.5">Data classification</label>
              <select
                className="input !w-52 !py-1.5"
                value={e.data_classification}
                disabled={saving}
                onChange={(ev) => void patch({ data_classification: ev.target.value })}
              >
                {DATA_CLASSIFICATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      <nav className="mt-6 flex gap-1 overflow-x-auto border-b border-slate-800">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setSearchParams(t === 'Overview' ? {} : { tab: t })}
            className={`whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
              tab === t
                ? 'border border-b-0 border-slate-800 bg-slate-900 text-brand-300'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {t}
            {t === 'Use Cases' && bundle.counts.useCases > 0 && <span className="ml-1.5 text-xs text-slate-500">{bundle.counts.useCases}</span>}
            {t === 'NIST' && bundle.counts.nistMappings > 0 && <span className="ml-1.5 text-xs text-slate-500">{bundle.counts.nistMappings}</span>}
            {t === 'Risks' && bundle.counts.openRisks > 0 && <span className="ml-1.5 text-xs text-slate-500">{bundle.counts.openRisks}</span>}
            {t === 'Deliverables' && bundle.artifacts.length > 0 && <span className="ml-1.5 text-xs text-slate-500">{bundle.artifacts.length}</span>}
          </button>
        ))}
      </nav>

      <div className="py-5">
        {tab === 'Overview' && <OverviewTab bundle={bundle} reload={reload} />}
        {tab === 'Assessment' && <AssessmentTab engagementId={e.id} reload={reload} />}
        {tab === 'Use Cases' && <UseCasesTab engagementId={e.id} reload={reload} />}
        {tab === 'NIST' && <NistTab engagementId={e.id} reload={reload} />}
        {tab === 'Risks' && <RisksTab engagementId={e.id} reload={reload} />}
        {tab === 'Deliverables' && <DeliverablesTab bundle={bundle} reload={reload} />}
      </div>
    </div>
  );
}
